import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import React, { useEffect, useState } from 'react'
import { useMergeHighlightModelStore, usePDFAnnotationStore } from '../../../store/pdf-pin.store';
import { AnnotationSolutionListEntity, PdfHighlighterEntity, PointsEntity, SystemDDLEntityForHighlights } from '../../../interface/pdf-pin';
import { FINAL_COLOR, LINK_COLOR, simplifyPathFromPolygraph, svgUrlToPngBytes } from '../utils/pdf-pin.shared';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { IsNullOrEmptyArray, IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { fetchStoredWaltzSolutions } from '../../../service/shared.service';
import { useParams } from 'react-router-dom';
import { SolutionDetails } from '../../../interface/order';
import { SolutionCategoryEnum } from '../../../utils/enum';

const MergeSystemModel = () => {
    const [isAgreed, setIsAgreed] = useState(false);

    const { orderId }: any = useParams();

    const { deleteHighlight, removeHighlightFromPage, selectedPageId, updateHighlight, systemDDL, highlights, setIconCache, updateHighlightInPage } = usePDFAnnotationStore();

    const [selectedSolution, setSelectedSolution] = useState("");

    const { isOpen, onClose, overlapHighlights, currentHighlight } = useMergeHighlightModelStore();

    const [solutionList, setSolutionList] = useState<AnnotationSolutionListEntity[] | []>([]);

    const handleMerged = async () => {

        if (isAgreed) {

            const mainStroke = overlapHighlights[0];

            const draftHighlight = highlights.find((item) => item.isDraft);

            const systemDetail = systemDDL.find((item) => item.id === mainStroke.systemId);
            if (!systemDetail) return;


            // Convert to PNG bytes
            const iconPngBytes: any = await svgUrlToPngBytes(
                systemDetail.systemIcon,
                !IsStringNullEmptyOrUndefined(selectedSolution) ? LINK_COLOR : FINAL_COLOR,
                60,
                60
            );

            // Create image object and wait for load
            const blob = new Blob([iconPngBytes], { type: "image/png" });
            const url = URL.createObjectURL(blob);

            await new Promise<void>((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    setIconCache(mainStroke.id, img);
                    resolve();
                };
                img.onerror = reject;
                img.src = url;
            });


            const otherStrokes: PdfHighlighterEntity[] = overlapHighlights
                .filter(s => s.id !== mainStroke.id);

            // Prepare list of point arrays in merge order
            const strokesToMerge: { x: number | null; y: number | null }[][] = [
                mainStroke.points,
                ...otherStrokes.map(s => s.points),
                currentHighlight
            ].map(points => simplifyPathFromPolygraph(points, 1)); // simplify each path

            const connectThreshold = 1;

            // Merge all points with gap logic
            let mergedPoints: { x: number | null; y: number | null }[] = [];

            for (let i = 0; i < strokesToMerge.length; i++) {
                const pts = strokesToMerge[i];

                if (mergedPoints.length === 0) {
                    mergedPoints = [...pts];
                } else {
                    // Check distance between last merged point and first new point
                    const lastPoint = [...mergedPoints].reverse().find(p => p.x != null && p.y != null);
                    const firstPoint = pts.find(p => p.x != null && p.y != null);

                    const dist = lastPoint && firstPoint
                        ? Math.hypot(lastPoint.x! - firstPoint.x!, lastPoint.y! - firstPoint.y!)
                        : Infinity;

                    if (dist > connectThreshold) {
                        // Insert gap
                        mergedPoints = [...mergedPoints, { x: null, y: null }, ...pts];
                    } else {
                        // Directly connect
                        mergedPoints = [...mergedPoints, ...pts];
                    }
                }
            }

            // Delete all other strokes (excluding mainStroke)
            for (const stroke of otherStrokes) {
                deleteHighlight(stroke.id);
                removeHighlightFromPage(selectedPageId, stroke.id);
            }

            updateHighlight(mainStroke.id, {
                ...mainStroke,
                solutionId: selectedSolution,
                points: (mergedPoints as PointsEntity[]),
                color: !IsStringNullEmptyOrUndefined(selectedSolution) ? LINK_COLOR : FINAL_COLOR,
                isDraft: false,
            });

            updateHighlightInPage(selectedPageId, mainStroke.id, {
                ...mainStroke,
                solutionId: selectedSolution,
                points: (mergedPoints as PointsEntity[]),
                color: !IsStringNullEmptyOrUndefined(selectedSolution) ? LINK_COLOR : FINAL_COLOR,
                isDraft: false,
            });




            if (draftHighlight) {
                deleteHighlight(draftHighlight.id);
                removeHighlightFromPage(selectedPageId, draftHighlight.id);
            }
        }

        onClose()
    }


    const handleClose = () => {
        const draftHighlight = highlights.find((item) => item.isDraft);
        if (!draftHighlight) return;
        deleteHighlight(draftHighlight.id);
        removeHighlightFromPage(selectedPageId, draftHighlight.id);
        onClose()
    }



    const handleGetSolutionList = (): any[] => solutionList.filter((item) => overlapHighlights.some((el) => el.solutionId === item.solutionId))

    useEffect(() => {


        const waltzOrderDetail = fetchStoredWaltzSolutions(orderId);
        if (waltzOrderDetail && !IsNullOrEmptyArray(waltzOrderDetail)) {

            const storedSolutions: null | SolutionDetails[] | [] = waltzOrderDetail

            if (storedSolutions && !IsNullOrEmptyArray(storedSolutions)) {
                const result = storedSolutions.filter((item) => !item.isDeleted && item.category === SolutionCategoryEnum.WALTZ && overlapHighlights.some((el) => el.solutionId === item._id)).map((item) => {
                    if (item.category === SolutionCategoryEnum.WALTZ) {
                        let locationName = [item.solutionNo.toString(), item.floor, item.space, item.systemName].filter((item) => !IsStringNullEmptyOrUndefined(item));
                        return {
                            name: locationName.join("_"),
                            solutionId: item._id,
                            category: item.category,
                            title: [item.solutionNo, item.floor, item.space].join("_"),
                            disabled: false,
                            systemId: item.systemId
                        }
                    }
                });
                setSolutionList(() => (result as AnnotationSolutionListEntity[]));
            }
        }

    }, [])

    return (
        <Dialog visible={isOpen} header="Merge Highlights" onHide={onClose} draggable={false} resizable={false} style={{ width: "600px" }} closable={false}>
            <div className="row">
                <div className="col-12 mb-4">
                    <div className='d-flex  align-items-center gap-1'>
                        <Checkbox checked={isAgreed} onChange={(e) => setIsAgreed((prev) => e.checked as boolean)} />
                        <span className='mx-2'>Would you like to merge this highlight with overlapping highlights?</span>
                    </div>
                </div>
                {
                    isAgreed && !IsNullOrEmptyArray(handleGetSolutionList()) && (
                        <div className="col-12">
                            <div className="form-group">
                                <label style={{ fontSize: "14px" }}>
                                    SELECT SOLUTION
                                </label>
                                <div>
                                    <Dropdown
                                        className='w-100'
                                        value={selectedSolution}
                                        onChange={(e) => { setSelectedSolution(e.value) }}
                                        options={handleGetSolutionList()}
                                        optionLabel='name'
                                        optionValue='solutionId'
                                        placeholder='Select solution'
                                    />
                                </div>
                            </div>
                        </div>
                    )
                }
                <div className="col-12 ">
                    <div className='d-flex align-items-center justify-content-end'>
                        <div>
                            <button className="btn btn-cancel text-dark m-0" onClick={handleClose} >Cancel</button>
                        </div>
                        <div>
                            <button className="btn btn-primary mx-2" disabled={isAgreed && !IsNullOrEmptyArray(handleGetSolutionList()) && IsStringNullEmptyOrUndefined(selectedSolution)} onClick={handleMerged}>Save</button>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default MergeSystemModel